import React from "react"

import { NzDataGrid } from "@/components/nzDataGrid"
import { Page } from "@/components/page"
import { PageCard } from "@/components/page-card"
import { PerformanceGridLegend } from "@/components/performance"
import { useActiveOrganization } from "@/contexts"
import {
  DEFAULT_PAGE_SIZE,
  DataGridFill,
  useEditRow,
  useInfiniteScroll,
  useSaveGridModel,
} from "@/nzds/data-grid"
import { useDepartmentsSpraypaint } from "@/services/department"
import { useGetBuildingTypes } from "@/services/site"
import { useAvailableReportDates } from "@/services/useAvailableReportDates"
import { useYear } from "@/utils/date/use-year"
import { usePage } from "@/utils/page/use-page"
import type { DateTime } from "luxon"

import { Box } from "@mui/material"
import { useGridApiRef } from "@mui/x-data-grid-pro"
import type { GridSlotProps, GridSlots } from "@mui/x-data-grid-pro"

import type {
  SiteExplorerGridRecord,
  SiteExplorerRecord,
} from "../../services/use-get-site-explorer"
import { useGetSiteExplorer } from "../../services/use-get-site-explorer"
import { useComputedSiteRows } from "../utils"
import { useHandleProcessRowUpdate } from "../utils/use-handle-process-row-update"
import { SitesPerformanceGridToolbar } from "./sites-performance-grid-toolbar"
import {
  defaultColumnVisibilityModel,
  defaultPinnedColumns,
  defaultSortModel,
  usePerformanceGridColumns,
} from "./utils/use-performance-grid-columns/use-performance-grid-columns"

export const SitesPerformanceRoute: React.FC = () => {
  const gridApiRef = useGridApiRef()
  const { activeOrganization } = useActiveOrganization()
  const { availableReportDatesData } = useAvailableReportDates()

  const { buildingTypesData, buildingTypesIsFetching } = useGetBuildingTypes()
  const { departmentsSpraypaintData, departmentsSpraypaintIsFetching } =
    useDepartmentsSpraypaint({
      apiQueryOptions: {
        where: { organization_id: activeOrganization?.id },
      },
    })

  const {
    handleRowEditCancelClick,
    handleRowEditClick,
    handleRowEditSaveClick,
    handleRowEditStop,
    rowModesModel,
  } = useEditRow()
  const defaultColumns = usePerformanceGridColumns({
    buildingTypes: buildingTypesData,
    departments: departmentsSpraypaintData,
    handleRowEditCancelClick,
    handleRowEditClick,
    handleRowEditSaveClick,
    rowModesModel,
  })

  const { page } = usePage({
    id: "sitesPerformancePage",
  })

  const { dateRange, setYear } = useYear({
    id: "sitesPerformanceYear",
    bounds: availableReportDatesData,
  })

  const handleYearChange = (value: DateTime) => {
    setYear(value.year)
  }

  const {
    gridModel: { columnVisibilityModel, columns, pinnedColumns, sortModel },
    handleColumnOrderChange,
    handleColumnVisibilityModelChange,
    handlePinnedColumnsChange,
    handleSortModelChange,
  } = useSaveGridModel<SiteExplorerGridRecord>({
    gridName: "sitesPerformance",
    gridModel: {
      columns: defaultColumns,
      columnVisibilityModel: defaultColumnVisibilityModel,
      pinnedColumns: defaultPinnedColumns,
      sortModel: defaultSortModel,
    },
  })

  const isSiteExplorerQueryEnabled =
    Boolean(activeOrganization?.id) &&
    Boolean(sortModel[0].field) &&
    Boolean(sortModel[0].sort) &&
    dateRange.isValid

  const {
    siteExplorerData,
    siteExplorerFetchNextPage,
    siteExplorerHasNextPage,
    siteExplorerIsFetching,
    siteExplorerIsFetchingNextPage,
  } = useGetSiteExplorer({
    parameters: {
      query: {
        "filter[organization_id]": activeOrganization?.id,
        "filter[start_date][gte]": dateRange.start?.toISODate(),
        "filter[end_date][lte]": dateRange.end?.toISODate(),
        include_benchmarks: true,
        "page[size]": DEFAULT_PAGE_SIZE,
        sort: `${sortModel[0].sort === "asc" ? "" : "-"}${sortModel[0].field}`,
        "stats[average]": "count",
        "stats[efficient]": "count",
        "stats[inefficient]": "count",
        "stats[page]": "count",
        "stats[very_efficient]": "count",
        "stats[very_inefficient]": "count",
      },
    },
    initialPage: page,
    queryOptions: {
      enabled: isSiteExplorerQueryEnabled,
    },
  })

  const rows = useComputedSiteRows<SiteExplorerRecord, SiteExplorerGridRecord>({
    data: siteExplorerData,
  })

  const { handleOnRowsScrollEnd } = useInfiniteScroll({
    fetchNextPage: siteExplorerFetchNextPage,
    hasNextPage: siteExplorerHasNextPage,
    isEnabled: isSiteExplorerQueryEnabled,
    isFetchingNextPage: siteExplorerIsFetchingNextPage,
  })

  const { handleProcessRowUpdate } = useHandleProcessRowUpdate(
    departmentsSpraypaintData
  )

  const handleProcessRowUpdateError = React.useCallback((error: Error) => {
    console.error(error)
  }, [])

  return (
    <Page fullHeight>
      <PageCard
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          sx={{
            mb: 3,
          }}
        >
          <PerformanceGridLegend
            atBenchmarkCount={siteExplorerData?.pages[0]?.average}
            efficientCount={siteExplorerData?.pages[0]?.efficient}
            extremelyInefficientCount={
              siteExplorerData?.pages[0]?.very_inefficient
            }
            highlyEfficientCount={siteExplorerData?.pages[0]?.very_efficient}
            inefficientCount={siteExplorerData?.pages[0]?.inefficient}
            siteCount={siteExplorerData?.pages[0]?.total}
          />
        </Box>
        <DataGridFill>
          <NzDataGrid
            apiRef={gridApiRef}
            aria-label="Sites performance"
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            disableColumnFilter
            loading={
              buildingTypesIsFetching ||
              departmentsSpraypaintIsFetching ||
              siteExplorerIsFetching ||
              siteExplorerIsFetchingNextPage
            }
            onColumnOrderChange={handleColumnOrderChange}
            onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
            onPinnedColumnsChange={handlePinnedColumnsChange}
            onProcessRowUpdateError={handleProcessRowUpdateError}
            onRowEditStop={handleRowEditStop}
            onRowsScrollEnd={handleOnRowsScrollEnd}
            onSortModelChange={handleSortModelChange}
            pinnedColumns={pinnedColumns}
            processRowUpdate={handleProcessRowUpdate}
            rowModesModel={rowModesModel}
            rows={rows}
            slotProps={{
              basePopper: {
                placement: "bottom-start",
              },
              toolbar: {
                availableDateRange: availableReportDatesData,
                dateRange,
                onYearChange: handleYearChange,
                organizationId: activeOrganization?.id,
              } as GridSlotProps["toolbar"],
            }}
            slots={{
              toolbar: SitesPerformanceGridToolbar as GridSlots["toolbar"],
            }}
            sortingMode="server"
            sortModel={sortModel}
          />
        </DataGridFill>
      </PageCard>
    </Page>
  )
}
